// ** React Imports
import { useCallback, useMemo } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

// ** Custom Hooks
import useOpenGraphMetaTags from "hooks/useOpenGraphMetaTags";
import useQuery from "hooks/useQuery";

// ** Custom Filters
import { compute as filterPeriodCompute } from "hooks/filters/useFilterPeriodExtended";

// ** Config
import configGlobal from "configuration";

// #####################################################

const usePostsMetaTags = ({ minYear }) => {
	const { t } = useTranslation(["common", "filtersAndSelectors", "pages"]);

	// ** Odczytanie parametrów z adresu URL
	const { sex: filterSexParam = null, period: filterPeriodParam = null } =
		useQuery();

	const language = useSelector((state) => state.globalCommon.language);

	const selectedSexTranslatedString = useMemo(() => {
		return (
			filterSexParam &&
			filterSexParam
				.split("-")
				.map((singleSexFromParam) =>
					t(`common:sexOptions.${singleSexFromParam}`)
				)
				.join(", ")
		);
	}, [filterSexParam, language]);

	const selectedPeriodTranslatedString = useMemo(() => {
		let filterPeriod = "";

		if (filterPeriodParam === "total") {
			filterPeriod = `${t(
				`filtersAndSelectors:period.total`
			)} ${minYear} - ${new Date().getFullYear()}`;
		} else if (filterPeriodParam === "latest") {
			filterPeriod = t(`filtersAndSelectors:period.latest`);
		} else if (filterPeriodParam) {
			const periodApiParams =
				filterPeriodCompute.urlToApiParam(filterPeriodParam);
			if (
				periodApiParams.day &&
				periodApiParams.month &&
				periodApiParams.year
			) {
				filterPeriod = filterPeriodParam.split("-").join(".");
			} else if (periodApiParams.month && periodApiParams.year) {
				const date = new Date();
				date.setMonth(
					filterPeriodCompute.urlToApiParam(filterPeriodParam).month -
						1
				);
				filterPeriod = `${t(
					`common:months.${dayjs(date).format("MMMM").toLowerCase()}`
				)} ${periodApiParams.year}`;
			} else if (periodApiParams.year) {
				filterPeriod = `${t(`filtersAndSelectors:period.total`)} ${
					periodApiParams.year
				}`;
			}
		}
		return filterPeriod;
	}, [filterPeriodParam, language]);

	// ** Tytuł Strony - Atrybut 'title'
	const title = t("pages:posts");

	// ** Meta Description
	const metaDescription = useMemo(() => {
		return `Umawiaj się na sex spotkania albo znajdź partnera na dłużej! Zobacz ${
			selectedPeriodTranslatedString
				? `posty z ${selectedPeriodTranslatedString}`
				: `posty z Najnowsze`
		}. Płeć: ${
			selectedSexTranslatedString || `Kobieta, Mężczyzna, Para, Trans`
		}.`;
	}, [selectedSexTranslatedString, selectedPeriodTranslatedString]);

	// ** Open Graph Meta Tags
	const ogMetaTags = useOpenGraphMetaTags({
		customTitle: `${title} - ${t("common:titleTemplate")}`,
		customDescription: metaDescription,
	});

	const canonicalLink = useCallback(() => {
		return (
			<link rel="canonical" href={`https://${configGlobal.host}/posts`} />
		);
	}, []);

	// #####################################################

	return { title, metaDescription, ogMetaTags, canonicalLink };
};

// #####################################################

export default usePostsMetaTags;

// #####################################################
